import React, { useState } from 'react';
import { Button, Collapse, Form, Table } from 'react-bootstrap';
import { Plus, Trash } from 'react-bootstrap-icons';
import { emptyExtractedQuoteItem } from '../../../utils/quoteExtractionMapping';

/**
 * @param {{ items: Array, onChange: (items: Array) => void, disabled?: boolean, isEncoFormat?: boolean }} props
 */
const EditableQuoteItemsTable = ({
  items = [],
  onChange,
  disabled = false,
  isEncoFormat = false,
}) => {
  const [expandedRaw, setExpandedRaw] = useState({});
  const [showAdvanced, setShowAdvanced] = useState(false);

  const updateRow = (index, patch) => {
    onChange(items.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  const deleteRow = index => {
    onChange(items.filter((_, i) => i !== index));
  };

  const addRow = () => {
    const nextLine =
      items.length > 0
        ? Math.max(...items.map(r => Number(r.lineNumber) || 0)) + (isEncoFormat ? 10 : 1)
        : isEncoFormat
          ? 10
          : 1;
    onChange([...items, emptyExtractedQuoteItem(nextLine)]);
  };

  const rowMissing = row => {
    const hasCatalog =
      String(row.catalogNumber || '').trim() !== '' ||
      String(row.productNumber || '').trim() !== '';
    const hasQty = row.quantity != null && row.quantity !== '';
    const hasDesc = String(row.description || '').trim() !== '';
    const hasUnitPrice = row.unitPrice != null && row.unitPrice !== '';
    const hasPrice =
      row.unitPrice != null ||
      row.unitPriceAfterDiscount != null ||
      row.lineTotal != null;

    if (isEncoFormat) {
      return !hasCatalog || !hasDesc || !hasQty || !hasUnitPrice;
    }
    const hasId = hasCatalog || hasDesc;
    return !hasId || !hasQty || !hasPrice;
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
        <h6 className="mb-0">Line items</h6>
        <div className="d-flex gap-2">
          {isEncoFormat && (
            <Button
              size="sm"
              variant="outline-secondary"
              type="button"
              onClick={() => setShowAdvanced(!showAdvanced)}
            >
              {showAdvanced ? 'Hide' : 'Show'} advanced details
            </Button>
          )}
          <Button
            size="sm"
            variant="outline-primary"
            type="button"
            disabled={disabled}
            onClick={addRow}
          >
            <Plus className="me-1" />
            Add row
          </Button>
        </div>
      </div>
      <div className="table-responsive">
        <Table bordered size="sm" className="mb-0 align-middle">
          <thead>
            <tr className="small text-center">
              <th>#</th>
              <th>Catalog</th>
              {showAdvanced && <th>Product #</th>}
              <th>Description</th>
              {showAdvanced && <th>CAS</th>}
              {!isEncoFormat && <th>ETA</th>}
              <th>Qty</th>
              <th>Unit</th>
              <th>Unit price</th>
              {!isEncoFormat && <th>Disc %</th>}
              {!isEncoFormat && <th>After disc.</th>}
              <th>Line total</th>
              {showAdvanced ? (
                <>
                  <th>Unit curr.</th>
                  <th>Line curr.</th>
                </>
              ) : (
                <th>Curr.</th>
              )}
              {showAdvanced && <th>Datasheet URL</th>}
              <th />
            </tr>
          </thead>
          <tbody>
            {items.map((row, index) => (
              <React.Fragment key={`item-${index}`}>
                <tr className={rowMissing(row) ? 'row-missing-required' : ''}>
                  <td>
                    <Form.Control
                      size="sm"
                      className="catalog-number number-value"
                      disabled={disabled}
                      value={row.lineNumber ?? index + 1}
                      onChange={e =>
                        updateRow(index, {
                          lineNumber:
                            e.target.value === '' ? null : Number(e.target.value),
                        })
                      }
                    />
                  </td>
                  <td>
                    <Form.Control
                      size="sm"
                      className="catalog-number product-number"
                      disabled={disabled}
                      value={row.catalogNumber ?? ''}
                      onChange={e => updateRow(index, { catalogNumber: e.target.value })}
                    />
                  </td>
                  {showAdvanced && (
                    <td>
                      <Form.Control
                        size="sm"
                        className="product-number"
                        disabled={disabled}
                        value={row.productNumber ?? ''}
                        onChange={e => updateRow(index, { productNumber: e.target.value })}
                      />
                    </td>
                  )}
                  <td>
                    <Form.Control
                      size="sm"
                      className="english-value"
                      disabled={disabled}
                      value={row.description ?? ''}
                      onChange={e => updateRow(index, { description: e.target.value })}
                    />
                  </td>
                  {showAdvanced && (
                    <td>
                      <Form.Control
                        size="sm"
                        className="cas-number"
                        disabled={disabled}
                        value={row.casNumber ?? ''}
                        onChange={e => updateRow(index, { casNumber: e.target.value })}
                      />
                    </td>
                  )}
                  {!isEncoFormat && (
                    <td>
                      <Form.Control
                        size="sm"
                        disabled={disabled}
                        value={row.eta ?? ''}
                        onChange={e => updateRow(index, { eta: e.target.value })}
                      />
                    </td>
                  )}
                  <td>
                    <Form.Control
                      size="sm"
                      type="number"
                      className="number-value"
                      disabled={disabled}
                      value={row.quantity ?? ''}
                      onChange={e =>
                        updateRow(index, {
                          quantity:
                            e.target.value === '' ? null : Number(e.target.value),
                        })
                      }
                    />
                  </td>
                  <td>
                    <Form.Control
                      size="sm"
                      disabled={disabled}
                      value={row.unit ?? ''}
                      onChange={e => updateRow(index, { unit: e.target.value })}
                    />
                  </td>
                  <td>
                    <Form.Control
                      size="sm"
                      type="number"
                      step="any"
                      className="currency-value number-value"
                      disabled={disabled}
                      value={row.unitPrice ?? ''}
                      onChange={e =>
                        updateRow(index, {
                          unitPrice: e.target.value === '' ? null : Number(e.target.value),
                        })
                      }
                    />
                  </td>
                  {!isEncoFormat && (
                    <td>
                      <Form.Control
                        size="sm"
                        type="number"
                        step="any"
                        disabled={disabled}
                        value={row.discountPercent ?? ''}
                        onChange={e =>
                          updateRow(index, {
                            discountPercent:
                              e.target.value === '' ? null : Number(e.target.value),
                          })
                        }
                      />
                    </td>
                  )}
                  {!isEncoFormat && (
                    <td>
                      <Form.Control
                        size="sm"
                        type="number"
                        step="any"
                        className="currency-value"
                        disabled={disabled}
                        value={row.unitPriceAfterDiscount ?? ''}
                        onChange={e =>
                          updateRow(index, {
                            unitPriceAfterDiscount:
                              e.target.value === '' ? null : Number(e.target.value),
                          })
                        }
                      />
                    </td>
                  )}
                  <td>
                    <Form.Control
                      size="sm"
                      type="number"
                      step="any"
                      className="currency-value number-value"
                      disabled={disabled}
                      value={row.lineTotal ?? ''}
                      onChange={e =>
                        updateRow(index, {
                          lineTotal: e.target.value === '' ? null : Number(e.target.value),
                        })
                      }
                    />
                  </td>
                  {showAdvanced ? (
                    <>
                      <td>
                        <Form.Control
                          size="sm"
                          className="currency-value"
                          disabled={disabled}
                          value={row.unitPriceCurrency ?? ''}
                          onChange={e =>
                            updateRow(index, { unitPriceCurrency: e.target.value })
                          }
                        />
                      </td>
                      <td>
                        <Form.Control
                          size="sm"
                          className="currency-value"
                          disabled={disabled}
                          value={row.lineTotalCurrency ?? ''}
                          onChange={e =>
                            updateRow(index, { lineTotalCurrency: e.target.value })
                          }
                        />
                      </td>
                    </>
                  ) : (
                    <td>
                      <Form.Control
                        size="sm"
                        className="currency-value"
                        disabled={disabled}
                        value={row.unitPriceCurrency ?? ''}
                        onChange={e =>
                          updateRow(index, {
                            unitPriceCurrency: e.target.value,
                          })
                        }
                      />
                    </td>
                  )}
                  {showAdvanced && (
                    <td>
                      <Form.Control
                        size="sm"
                        className="url-value english-value"
                        disabled={disabled}
                        value={row.productDataSheetUrl ?? ''}
                        onChange={e =>
                          updateRow(index, { productDataSheetUrl: e.target.value })
                        }
                      />
                    </td>
                  )}
                  <td className="text-center">
                    <Button
                      variant="link"
                      size="sm"
                      className="text-danger p-0"
                      type="button"
                      disabled={disabled}
                      onClick={() => deleteRow(index)}
                      title="Delete row"
                    >
                      <Trash />
                    </Button>
                  </td>
                </tr>
                {(row.rawLine || row.rawUrlLine) && (
                  <tr>
                    <td colSpan={16} className="py-1">
                      {row.rawLine && (
                        <>
                          <Button
                            variant="link"
                            size="sm"
                            className="p-0 text-muted"
                            type="button"
                            onClick={() =>
                              setExpandedRaw(prev => ({
                                ...prev,
                                [`line-${index}`]: !prev[`line-${index}`],
                              }))
                            }
                          >
                            raw line
                          </Button>
                          <Collapse in={expandedRaw[`line-${index}`]}>
                            <pre className="small english-value bg-light p-2 mb-0 mt-1">
                              {row.rawLine}
                            </pre>
                          </Collapse>
                        </>
                      )}
                      {row.rawUrlLine && (
                        <>
                          <Button
                            variant="link"
                            size="sm"
                            className="p-0 text-muted ms-2"
                            type="button"
                            onClick={() =>
                              setExpandedRaw(prev => ({
                                ...prev,
                                [`url-${index}`]: !prev[`url-${index}`],
                              }))
                            }
                          >
                            raw URL line
                          </Button>
                          <Collapse in={expandedRaw[`url-${index}`]}>
                            <pre className="small url-value bg-light p-2 mb-0 mt-1">
                              {row.rawUrlLine}
                            </pre>
                          </Collapse>
                        </>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </Table>
      </div>
      {items.length === 0 && (
        <p className="text-muted small">
          No line items were detected. You can add them manually.
        </p>
      )}
    </div>
  );
};

export default EditableQuoteItemsTable;
